import { useIsFocused } from '@react-navigation/native';
import dayjs from 'dayjs';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  DeviceEventEmitter,
  useWindowDimensions,
} from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { ProjectAvatar } from '@components/Avatar/ProjectAvatar';
import { WebModal } from '@components/Modals/WebModal.web';
import { WebErrorModal } from '@components/ProjectDetail/ProjectChecklist/WebErrorModal';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import {
  BaseButtonV3,
  CreateTaskButton,
} from '@components/TaskList/BaseButtonV3';
import { MyTasksDueDatePicker } from '@components/TaskList/MyTasksDueDatePicker.web';
import { PrioritySelector } from '@components/TaskList/PrioritySelector.web';
import { ProjectSelectV3 } from '@components/TaskList/ProjectSelectV3';
import { TaskAssigneeMembersButtonV3 } from '@components/TaskList/TaskAssigneeMembersButtonV3.web';
import { TaskCreatedToast } from '@components/TaskList/TaskCreatedToast.web';
import {
  StringSearchType,
  UserSearchType,
} from '@components/TaskList/TaskInviteOrAssignee.web';
import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3.web';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { TooltipOnboardingWeb } from '@components/Web/TooltipOnboarding.web';
import {
  TaskStatus,
  TaskPriority,
  Project,
  useCreateTaskMutation,
  Task,
  useUpdateOnboardingStateMutation,
  TaskMemberRole,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import theme from '@themes/theme';
import { beginningOfDayUTC, beginningOfToday } from '@utils/formatters/date';
import { markOnboardingInviteesAssigned } from '@utils/onboardingInvitedMembers';

const MIN_INPUT_HEIGHT = 16;
const MAX_INPUT_HEIGHT = 64;

// Max characters allowed for task name
const MAX_TASK_NAME_LENGTH = 255;
const DEFAULT_PRIORITY = TaskPriority.Medium;
const ONBOARDING_TOAST_MIN_WIDTH = 310;

/**
 * Whether the "Add a task" onboarding tooltip should show. Pure so it can be
 * tested directly. Returns false when suppressed by a higher-priority tooltip
 * (e.g. "You have tasks waiting"), so the two never overlap (TA-9958 #23).
 */
export const shouldShowAddTaskOnboardingTooltip = ({
  suppressOnboardingTooltip,
  showProjectCreatedModal,
  hasDefaultProject,
  welcomeModalSeen,
  taskCreationComplete,
}: {
  suppressOnboardingTooltip: boolean;
  showProjectCreatedModal: boolean;
  hasDefaultProject: boolean;
  welcomeModalSeen?: boolean | null;
  taskCreationComplete?: boolean | null;
}): boolean =>
  !suppressOnboardingTooltip &&
  !showProjectCreatedModal &&
  hasDefaultProject &&
  welcomeModalSeen === true &&
  taskCreationComplete === false;

export const MyTasksNewItem = ({
  onPressTask,
  defaultProject,
  suppressOnboardingTooltip = false,
}: {
  onPressTask?: (task: Task) => void;
  defaultProject?: Project;
  /**
   * When true, the "Add a task" onboarding tooltip is suppressed. Used so it
   * does not overlap the higher-priority "You have tasks waiting" tooltip
   * during onboarding (TA-9958 #23).
   */
  suppressOnboardingTooltip?: boolean;
}) => {
  const [currentPriority, setCurrentPriority] = useState(DEFAULT_PRIORITY);
  const [currentDueDate, setCurrentDueDate] = useState<Date | undefined>(
    undefined
  );

  const [taskName, setTaskName] = useState('');

  const [showErrorMsg, setShowErrorMsg] = useState<string | undefined>(
    undefined
  );
  const [showDiscard, setShowDiscard] = useState<boolean>(false);
  const [showInput, setShowInput] = useState(false);

  const { me } = useMe();
  const onboardingActive = useOnboardingActive();

  const [showOnboardingTaskNameTooltip, setShowOnboardingTaskNameTooltip] =
    useState(false);
  const [showTaskNameTooltip, setShowTaskNameTooltip] = useState(false);
  const tooltipOpacity = useRef(new Animated.Value(1)).current;
  const fadeStarted = useRef(false);

  const [updateOnboardingState] = useUpdateOnboardingStateMutation();
  const { showProjectCreatedModal } = useWebDrawer();

  const updateOnboardingStateTaskCreationComplete = async () => {
    await updateOnboardingState({
      variables: {
        attributes: {
          welcomeModalSeen: true,
          taskCreationComplete: true,
        },
      },
      onCompleted: () => {
        setTimeout(() => {
          DeviceEventEmitter.emit('SHOW_CHECKLIST_ONBOARDING_TOOLTIP');
        }, 2000);
      },
      update: (cache) => {
        if (!me?.id) return;
        cache.modify({
          id: cache.identify({ __typename: 'User', id: me.id }),
          fields: {
            onboardingState: (existing) => ({
              ...existing,
              welcomeModalSeen: true,
              taskCreationComplete: true,
            }),
          },
        });
      },
    });
  };

  const hasUpdatedRef = useRef(false);

  const safeUpdateOnboardingState = () => {
    if (hasUpdatedRef.current || me?.onboardingState?.taskCreationComplete)
      return;
    hasUpdatedRef.current = true;
    updateOnboardingStateTaskCreationComplete();
  };

  const isFocused = useIsFocused();
  useEffect(() => {
    if (
      !isFocused &&
      showOnboardingTaskNameTooltip &&
      me?.onboardingState?.taskCreationComplete === false
    ) {
      safeUpdateOnboardingState();
    }
  }, [
    isFocused,
    showOnboardingTaskNameTooltip,
    me?.onboardingState?.taskCreationComplete,
  ]);
  useEffect(() => {
    if (
      onboardingActive &&
      shouldShowAddTaskOnboardingTooltip({
        suppressOnboardingTooltip,
        showProjectCreatedModal,
        hasDefaultProject: !!defaultProject,
        welcomeModalSeen: me?.onboardingState?.welcomeModalSeen,
        taskCreationComplete: me?.onboardingState?.taskCreationComplete,
      })
    ) {
      setShowOnboardingTaskNameTooltip(true);
    } else if (
      onboardingActive &&
      defaultProject &&
      me?.onboardingState?.welcomeModalSeen === true &&
      me?.onboardingState?.taskCreationComplete === true &&
      me?.onboardingState?.checklistCreationComplete === false
    ) {
      setShowOnboardingTaskNameTooltip(false);
      setTimeout(() => {
        DeviceEventEmitter.emit('SHOW_CHECKLIST_ONBOARDING_TOOLTIP');
      }, 2000);
    } else {
      setShowOnboardingTaskNameTooltip(false);
    }
  }, [
    onboardingActive,
    suppressOnboardingTooltip,
    defaultProject,
    me?.onboardingState?.welcomeModalSeen,
    me?.onboardingState?.taskCreationComplete,
    showProjectCreatedModal,
  ]);

  useEffect(() => {
    if (showOnboardingTaskNameTooltip && !showTaskNameTooltip) {
      setShowInput(true);
      setTimeout(() => {
        setShowTaskNameTooltip(true);
        tooltipOpacity.setValue(1);
        fadeStarted.current = false;
      }, 800);
    }
  }, [showOnboardingTaskNameTooltip]);

  // Fade tooltip immediately over 4s when typing (only once)
  useEffect(() => {
    if (taskName.length > 0 && showTaskNameTooltip && !fadeStarted.current) {
      fadeStarted.current = true;
      Animated.timing(tooltipOpacity, {
        toValue: 0,
        duration: 3000, // fade out over 3 seconds
        useNativeDriver: true,
      }).start(() => {
        setShowTaskNameTooltip(false);
        DeviceEventEmitter.emit('TASK_NAME_TOOLTIP_INACTIVE');
      });
    }
  }, [taskName.length, showTaskNameTooltip, tooltipOpacity]);

  // Container height animation for the whole input area
  const animatedHeight = useRef(new Animated.Value(0)).current;

  const [showSelectProject, setShowSelectProject] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>(
    defaultProject
  );
  const [dueDateModalShowing, setDueDateModalShowing] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [prioritySelectorShowing, setPrioritySelectorShowing] = useState(false);
  const [selectedUserList, setSelectedUserList] = useState<UserSearchType[]>(
    []
  );

  // ContentEditable div ref (web only)
  const inputRef = useRef<HTMLDivElement | null>(null);

  // Animate container expand / collapse when showInput changes
  useEffect(() => {
    if (showInput) {
      Animated.timing(animatedHeight, {
        toValue: 64,
        duration: 250,
        useNativeDriver: false,
      }).start();
    } else {
      Animated.timing(animatedHeight, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false,
      }).start();
    }
  }, [showInput, animatedHeight]);

  // Recalculate container height based on content height (grow & shrink)
  useEffect(() => {
    if (showInput) {
      const scrollHeight = inputRef?.current?.scrollHeight ?? 0;

      Animated.timing(animatedHeight, {
        toValue: Math.min(
          108, // max container height
          Math.max(64, scrollHeight + 48) // base 64, +16 per extra line approx (depending on your font)
        ),
        duration: 250,
        useNativeDriver: false,
      }).start();
    }
  }, [taskName, showInput, animatedHeight]);

  const [showTooltip, setShowTooltip] = useState(false);
  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showTooltip]);

  const { t } = useTranslation();
  const [createTask] = useCreateTaskMutation();
  const { width: screenWidth } = useWindowDimensions();

  const [displayButtonTitle, setDisplayButtonTitle] = useState(
    screenWidth > 1280
  );
  useEffect(() => {
    setDisplayButtonTitle(screenWidth > 1280);
  }, [screenWidth]);

  const toast = useToast();

  // Show toast after creating task
  const showToast = (
    title: string,
    msg: string,
    onPress: () => void,
    onClose: () => void,
    minWidth?: number
  ) => {
    return toast.show(
      <TaskCreatedToast
        minWidth={minWidth}
        title={title}
        message={msg}
        duration={3000}
        onPress={() => {
          onPress();
        }}
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: {
          marginBottom: 39,
          backgroundColor: 'transparent',
          shadowColor: 'transparent',
        },
        onClose: () => {
          onClose();
        },
      }
    );
  };

  // Create task mutation and reset state
  const doCreateTask = () => {
    if (!taskName.trim()) {
      setShowErrorMsg('Task Name is required.');
      return;
    }
    if (selectedProject === undefined) {
      setShowTooltip(true);
      return;
    }

    let taskStatus = TaskStatus.Ongoing;
    if (currentDueDate) {
      const today = beginningOfToday();
      const isToday = dayjs().isSame(currentDueDate, 'day');
      const isPast = currentDueDate < today;
      const isFuture = currentDueDate > today;

      if (isPast) taskStatus = TaskStatus.Overdue;
      else if (isFuture || isToday) taskStatus = TaskStatus.NotStarted;
    }

    const emailList = selectedUserList.filter(
      (t) => t.__typename === 'StringSearchType'
    );
    const userList = selectedUserList.filter(
      (t) => t.__typename !== 'StringSearchType' && !t.isTaskTagUser
    );

    const tasktagUserList = selectedUserList.filter(
      (t) => t.__typename !== 'StringSearchType' && t.isTaskTagUser
    );

    const attributes = {
      name: taskName.trim(),
      description: '',
      dependencyIds: [],
      projectId: selectedProject.id,
      phaseId: '',
      priority: currentPriority,
      status: taskStatus,
      members: userList.map((m) => ({
        userId: m.id,
        role: m.role,
      })),
      taskMembers: [
        ...emailList.map((m) => ({
          email: m.title,
          role: m.role,
        })),
        ...tasktagUserList.map((m) => ({
          email: m.email,
          role: m.role,
        })),
      ],
      skillIds: [],
      startDate: null,
      dueDate: currentDueDate
        ? beginningOfDayUTC(currentDueDate).toISOString()
        : null,
      checklists: [],
      completedAt: null,
    };

    createTask({
      variables: { attributes },
      refetchQueries: ['listTasks', 'listProjects'],
      onCompleted: (data) => {
        const noTasks =
          defaultProject &&
          defaultProject?.activeTasksCount === 0 &&
          defaultProject?.upcomingTasksCount === 0 &&
          defaultProject?.overdueTasksCount === 0 &&
          defaultProject?.completedTasksCount === 0;
        const toastId =
          noTasks && showOnboardingTaskNameTooltip
            ? showToast(
                t('models:tasks.onboardingToast.title'),
                t('models:tasks.onboardingToast.message', {
                  projectName: defaultProject?.name ?? 'The Project',
                }),
                () => {
                  setTimeout(() => toast.hide(toastId), 0);
                },
                () => {
                  showOnboardingTaskNameTooltip && safeUpdateOnboardingState();
                },
                ONBOARDING_TOAST_MIN_WIDTH
              )
            : showToast(
                '',
                taskName.trim(),
                () => {
                  setTimeout(() => toast.hide(toastId), 0);
                  if (data.createTask) onPressTask?.(data.createTask);
                  showOnboardingTaskNameTooltip && safeUpdateOnboardingState();
                },
                () => {
                  showOnboardingTaskNameTooltip && safeUpdateOnboardingState();
                }
              );

        DeviceEventEmitter.emit('TASK_CREATED');

        // TA-10892 #33: flag everyone assigned in the onboarding task; the
        // chat list reacts to invited-and-assigned members once
        // taskCreationComplete is set.
        if (showOnboardingTaskNameTooltip) {
          const isAssignee = (m: { role?: string | null }) =>
            m.role === TaskMemberRole.Assignee;
          markOnboardingInviteesAssigned([
            ...userList.filter(isAssignee).map((m) => ({ id: m.id })),
            ...(emailList as StringSearchType[])
              .filter(isAssignee)
              .map((e) => ({ email: e.title })),
            ...tasktagUserList.filter(isAssignee).map((u) => ({
              id: u.id,
              email: (u as { email?: string | null }).email ?? undefined,
            })),
          ]);
        }

        if (showOnboardingTaskNameTooltip && defaultProject?.members) {
          const projectMemberIds = new Set(
            defaultProject.members.map((m) => m.user.id)
          );
          const seenEmails = new Set<string>();
          const nonMemberAssignees: {
            email: string;
            id?: string;
            name?: string;
            avatarId?: string;
          }[] = [];

          // Keep assignees stored by earlier tasks in this project so a second
          // unjoined assignee extends the Got-crew banner instead of replacing
          // the first one (TA-10892 #32).
          if (
            defaultProject.id &&
            globalThis.localStorage?.getItem(
              'non_member_assignee_emails_project_id'
            ) === defaultProject.id.toString()
          ) {
            try {
              const stored = JSON.parse(
                globalThis.localStorage?.getItem(
                  'non_member_assignee_emails'
                ) ?? '[]'
              ) as unknown;
              if (Array.isArray(stored)) {
                stored.forEach((a) => {
                  const entry = typeof a === 'string' ? { email: a } : a;
                  const email =
                    typeof entry?.email === 'string'
                      ? entry.email.toLowerCase()
                      : '';
                  if (email && !seenEmails.has(email)) {
                    seenEmails.add(email);
                    nonMemberAssignees.push({ ...entry, email });
                  }
                });
              }
            } catch {
              // Corrupt entry — start over with this task's assignees.
            }
          }

          // Email-only invites (StringSearchType)
          (emailList as StringSearchType[]).forEach((e) => {
            const email = e.title.toLowerCase();
            if (email && !seenEmails.has(email)) {
              seenEmails.add(email);
              nonMemberAssignees.push({ email });
            }
          });

          // Tasktag users not yet in project — include id and name
          tasktagUserList
            .filter((u) => u.id && !projectMemberIds.has(u.id))
            .forEach((u) => {
              const email = (
                (u as { email?: string | null }).email ?? ''
              ).toLowerCase();
              if (email && !seenEmails.has(email)) {
                seenEmails.add(email);
                nonMemberAssignees.push({
                  email,
                  id: u.id,
                  name: (u as { name?: string | null }).name ?? undefined,
                  avatarId:
                    (u as { avatar?: { id?: string } | null }).avatar?.id ??
                    undefined,
                });
              }
            });

          if (nonMemberAssignees.length > 0 && defaultProject.id) {
            globalThis.localStorage?.setItem(
              'non_member_assignee_emails_project_id',
              defaultProject.id.toString()
            );
            globalThis.localStorage?.setItem(
              'non_member_assignee_emails',
              JSON.stringify(nonMemberAssignees)
            );
            DeviceEventEmitter.emit('NON_MEMBER_ASSIGNEES_UPDATED');
          } else if (data.createTask?.id) {
            globalThis.sessionStorage?.setItem(
              'non_member_task_id',
              data.createTask.id
            );
          }
        }

        setCurrentDueDate(undefined);
        setSelectedUserList([]);
        setSelectedProject(defaultProject);
        setTaskName('');
        setCurrentPriority(DEFAULT_PRIORITY);
        setShowInput(false);

        // Clear visual content of the contentEditable div
        if (inputRef.current) {
          inputRef.current.innerText = '';
        }
      },
      onError: (error) => {
        setShowErrorMsg(error.message || t('models:tasks.createTaskError'));
        safeUpdateOnboardingState();
      },
      // Code review fix: onError above already shows a generic error
      // message for any error — opt out of the global PERMISSION_DENIED/
      // CONFLICT toast so this doesn't double up.
      context: { suppressGlobalMutationErrorToast: true },
    });
  };

  // Fade-in animation for placeholder text when input is hidden
  const fadeAnim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!showInput) {
      fadeAnim.setValue(0);
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  }, [showInput, fadeAnim]);

  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // Detect if project name text is truncated (for tooltip)
  useEffect(() => {
    if (!displayButtonTitle) {
      setIsTruncated(true);
      return;
    }
    const el = textRef.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [selectedProject, displayButtonTitle]);

  // Check if there are unsaved changes before discarding
  const checkPageStatus = () => {
    const isProjectDirty = defaultProject
      ? selectedProject?.id !== defaultProject.id
      : !!selectedProject;
    if (
      taskName !== '' ||
      isProjectDirty ||
      currentPriority !== DEFAULT_PRIORITY ||
      currentDueDate ||
      selectedUserList.length > 0
    ) {
      setShowDiscard(true);
    } else {
      setShowInput(false);
    }
  };

  const dismissOutsideClick =
    !showInput ||
    showSelectProject ||
    showAddMembersModal ||
    showErrorMsg ||
    showDiscard ||
    showTooltip ||
    dueDateModalShowing ||
    prioritySelectorShowing;

  const handleClickOutside = useCallback(() => {
    checkPageStatus();
  }, [
    defaultProject,
    selectedProject,
    taskName,
    currentPriority,
    currentDueDate,
    selectedUserList,
  ]);

  const containerRef = useOutsideClick<HTMLDivElement>(handleClickOutside, {
    eventType: 'mousedown',
    enabled: !dismissOutsideClick,
  });

  // ESC key handler
  useEffect(() => {
    if (dismissOutsideClick) return;

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowDiscard(true);
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [dismissOutsideClick]);

  const selectProjectCom = (
    <Box
      width={displayButtonTitle ? 128 : 32}
      mx={displayButtonTitle ? 'm' : 'none'}
      flexDirection='row'
      height={32}
      justifyContent='center'
      alignItems='center'>
      {selectedProject ? (
        <ProjectAvatar
          icon={selectedProject?.icon}
          color={selectedProject?.color}
        />
      ) : (
        <Icon
          name='Office'
          variant='xxs'
          color={showTooltip ? 'alertRed' : 'grey05'}
        />
      )}

      {displayButtonTitle && (
        <Text
          ref={textRef}
          flexShrink={1}
          ml='xxs'
          ellipsizeMode='tail'
          accessibilityLabel='Select Project'
          variant='webBodyHintSubline'
          color={showTooltip ? 'alertRed' : 'textSecondary'}
          numberOfLines={1}>
          {selectedProject ? selectedProject.name : 'Select Project'}
        </Text>
      )}
    </Box>
  );

  // Detect if current content should be treated as multi-line
  // const isMultiLine = taskName.includes('\n');

  // Handle contentEditable input changes (including paste)
  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    let text = e.currentTarget.innerText || '';
    text = text.replace(/\r\n/g, '\n');
    if (text === '\n') {
      text = '';
    }

    // Enforce max length when user types or pastes
    if (text.length > MAX_TASK_NAME_LENGTH) {
      text = text.slice(0, MAX_TASK_NAME_LENGTH);
      e.currentTarget.innerText = text;

      // Move caret to the end after trimming
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(e.currentTarget);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }

    setTaskName(text);
  }, []);

  // Handle Enter / Shift+Enter behavior in contentEditable + length limit on keydown
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Submit on Enter (without Shift)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        doCreateTask();
        return;
      }

      // If we already reached max length, block further text input
      const isTextKey =
        e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;

      if (isTextKey && taskName.length >= MAX_TASK_NAME_LENGTH) {
        // Allow navigation / deletion only; block new characters
        e.preventDefault();
      }
    },
    [doCreateTask, taskName.length]
  );

  // Auto focus contentEditable when input is shown
  useEffect(() => {
    if (!showInput || !inputRef.current) return;

    const el = inputRef.current;

    // Delay to ensure element is rendered
    const timer = setTimeout(() => {
      el.focus();

      // Move cursor to the end of the content
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }, 10);

    return () => clearTimeout(timer);
  }, [showInput]);

  const [showCreateButton, setShowCreateButton] = useState(false);
  // Ensure contentEditable is cleared when taskName is reset programmatically
  useEffect(() => {
    // if (taskName === '' && inputRef.current && showInput === false) {
    //   inputRef.current.innerText = '';
    // }
    // if (showInput && taskName.length > 0 && inputRef.current) {
    //   setShowCreateButton(true);
    // } else {
    //   setShowCreateButton(false);
    // }
    const hasContent = taskName.replace(/\s/g, '').length > 0;
    setShowCreateButton(showInput && hasContent);
  }, [taskName, showInput]);
  return (
    <>
      <div ref={containerRef}>
        <Box
          minHeight={56}
          maxHeight={108}
          px='m'
          borderTopColor='grey02'
          borderTopWidth={1}
          justifyContent='center'
          alignItems='flex-start'>
          {showInput ? (
            <Animated.View
              style={{
                overflow: 'hidden',
                height: animatedHeight,
                justifyContent: 'center',
                width: '100%',
              }}>
              <Box
                width='100%'
                flexDirection='row'
                alignItems='center'
                accessibilityLabel='Search'>
                <TooltipOnboardingWeb
                  variant='bottom-left'
                  tooltipStyle='success'
                  title={t('models:tasks.tooltip.addTaskTitle')}
                  description={t('models:tasks.tooltip.addTaskDescription')}
                  open={showTaskNameTooltip}
                  forceShow={showTaskNameTooltip}
                  animatedOpacity={tooltipOpacity}
                  offset={16}
                  style={{ alignSelf: 'stretch' }}>
                  <Box width={374}>
                    {/* ContentEditable input area */}
                    <div
                      ref={inputRef}
                      contentEditable
                      suppressContentEditableWarning
                      onInput={handleInput}
                      onKeyDown={handleKeyDown}
                      aria-label='Enter task name'
                      // Important: style is pure CSS, not RN style here
                      style={{
                        outline: 'none',
                        cursor: 'text',
                        minHeight: MIN_INPUT_HEIGHT,
                        maxHeight: MAX_INPUT_HEIGHT,
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        // Single-line: vertically center text
                        // Multi-line: align to top
                        display: 'block',
                        // display: 'flex',
                        // alignItems: isMultiLine ? 'flex-start' : 'center',
                        // Typography from theme
                        fontSize: theme.textVariants.webBodySecondary.fontSize,
                        fontFamily:
                          theme.textVariants.webBodySecondary.fontFamily,
                        lineHeight:
                          theme.textVariants.webBodySecondary.lineHeight + 'px',
                        color: theme.colors.textSecondary,
                      }}
                      // Placeholder simulation (native contentEditable does not support placeholder)
                      data-placeholder='Enter task name...'
                    />

                    {/* Helper text */}
                    <Text variant='webBodyHintSubline' color='grey04' mt='xxs'>
                      {t('models:projects.checklist.pressShiftEnterForNewLine')}
                    </Text>
                  </Box>
                </TooltipOnboardingWeb>

                <Box flex={1} mr='s' />
                {showCreateButton && (
                  <Box flexDirection='row'>
                    {!defaultProject && (
                      <BaseButtonV3
                        accessibilityLabel='Select project'
                        buttonBorderColor={showTooltip ? 'alertRed' : 'grey02'}
                        mr='s'
                        onPress={() => {
                          setShowTooltip(false);
                          setShowSelectProject(true);
                        }}>
                        {!selectedProject && (
                          <TooltipWebV3
                            marginOffset={4}
                            open={showTooltip}
                            title={
                              <Box height={32} width={174}>
                                <Text variant='labelSmall' color='white'>
                                  No Project Selected
                                </Text>
                                <Text
                                  variant='webBodyHintSubline'
                                  color='white'>
                                  Please select one to continue
                                </Text>
                              </Box>
                            }
                            placement='bottom'>
                            <span>{selectProjectCom}</span>
                          </TooltipWebV3>
                        )}

                        {selectedProject && (
                          <TooltipWebV3
                            marginOffset={5}
                            disableHoverListener={
                              !selectedProject || !isTruncated
                            }
                            disableFocusListener={
                              !selectedProject || !isTruncated
                            }
                            disableTouchListener={
                              !selectedProject || !isTruncated
                            }
                            title={
                              <Text variant='labelSmall' color='white'>
                                {selectedProject?.name ?? 'No Project Selected'}
                              </Text>
                            }
                            placement='bottom'>
                            <div>{selectProjectCom}</div>
                          </TooltipWebV3>
                        )}
                      </BaseButtonV3>
                    )}

                    <BaseButtonV3
                      accessibilityLabel='Select priority'
                      onModalShow={(showing) => {
                        setPrioritySelectorShowing(showing);
                      }}
                      mr='s'
                      modalRadius={8}
                      popModalComponent={({ onClose }) => (
                        <PrioritySelector
                          onPrioritySelect={(priority: TaskPriority) => {
                            setCurrentPriority(priority);

                            onClose();
                          }}
                        />
                      )}>
                      {currentPriority === TaskPriority.High && (
                        <Icon name='ChevronsUp' color='alertRed' variant='s' />
                      )}
                      {currentPriority === TaskPriority.Low && (
                        <Icon name='ChevronsDown' color='blue' variant='s' />
                      )}
                      {currentPriority === TaskPriority.Medium && (
                        <Icon name='Equals' color='pastelYellow' variant='s' />
                      )}
                    </BaseButtonV3>

                    <MyTasksDueDatePicker
                      accessibilityLabel='Select due date'
                      onModalShow={(showing) => {
                        setDueDateModalShowing(showing);
                      }}
                      hideTitle={!displayButtonTitle}
                      selectedDate={currentDueDate}
                      title='Due Date'
                      boxProps={{
                        position: 'bottom',
                        offset: [0, 4],
                      }}
                      onSelectDate={(d) => {
                        setCurrentDueDate(d);
                      }}
                    />
                    <TaskAssigneeMembersButtonV3
                      accessibilityLabel='Assign task members'
                      selectedUser={selectedUserList}
                      onAssignMember={setSelectedUserList}
                      onSetShowInviteOrAssignModal={setShowAddMembersModal}
                    />
                    <CreateTaskButton
                      accessibilityLabel='Create task'
                      onPress={() => {
                        doCreateTask();
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Animated.View>
          ) : (
            <Animated.View style={{ opacity: fadeAnim }}>
              <Text
                variant='webBodySecondary'
                accessibilityLabel='Task'
                style={{ letterSpacing: 0.02 }}
                color='grey04'
                onPress={() => {
                  setShowInput(true);
                }}
                numberOfLines={1}>
                Enter task name...
              </Text>
            </Animated.View>
          )}
        </Box>
      </div>

      {/* --- Modals --- */}
      {showSelectProject && (
        <WebModal
          accessibilityLabel='Select Project'
          title='Select Project'
          onClose={() => {
            setShowSelectProject(false);
          }}
          visible={showSelectProject}
          width={504}>
          <ProjectSelectV3
            selectedProjectId={selectedProject?.id}
            onSelectFinish={(project) => {
              setSelectedProject(project);
            }}
            onClose={() => {
              setShowSelectProject(false);
            }}
          />
        </WebModal>
      )}

      {showErrorMsg && (
        <WebErrorModal
          onClose={() => {
            setShowErrorMsg(undefined);
          }}
          onGotIt={() => {
            setShowErrorMsg(undefined);
            if (showErrorMsg?.includes('Task Name')) {
              setTimeout(() => {
                inputRef.current?.focus();
              }, 1);
            } else if (showErrorMsg?.includes('project')) {
              setTimeout(() => {
                setShowSelectProject(true);
              }, 1);
            }
          }}
          errorMsg={showErrorMsg}
          errorTitle='Create Task'
          modalVisible={showErrorMsg}
        />
      )}
      {showDiscard && (
        <WebErrorModal
          onClose={() => {
            setShowDiscard(false);
          }}
          onGotIt={() => {
            setShowDiscard(false);
            setShowInput(false);
            setCurrentDueDate(undefined);
            setSelectedUserList([]);
            setSelectedProject(defaultProject);
            setTaskName('');
            setCurrentPriority(DEFAULT_PRIORITY);
            // Clear contentEditable on discard as well
            if (inputRef.current) {
              inputRef.current.innerText = '';
            }
          }}
          errorMsg={t('models:discardTask.message')}
          errorTitle={t('models:discardTask.title')}
          buttonTitle={t('models:chat.imageMarkup.discard')}
          modalVisible={showDiscard}
        />
      )}
    </>
  );
};
